// The signed policy bundle (spec/10 §6.2, spec/11 §4, §6; F-STN-09).
//
// Everything a station needs in order to behave the way its venue configured it arrives as one
// **policy bundle**: the resolved locker and cleanup settings, the offline policy, the low-time
// warning thresholds, the attract media list and the library version (spec/10 §6.2). The bundle is
// JSON, **signed by the cloud with Ed25519** — the same api JWKS that signs the device, realtime and
// kiosk tokens (spec/07 §2.1, §5), so the station verifies it with the one trust anchor it already
// authenticates against and no new key is introduced. It is versioned, cached in `station.db`, and
// applied transactionally with read-back verification (spec/11 §6); an invalid or unverifiable bundle
// leaves the previous one in force and the screen locked (spec/11 §1, fail closed).
//
// ---------------------------------------------------------------------------------------------
// The signed envelope
// ---------------------------------------------------------------------------------------------
//
// The signed bundle on the wire is a **compact JWS** (an EdDSA JWT), minted by the api's `jwt` plugin
// exactly as the device and kiosk tokens are (`apps/server/src/modules/device/tokens.ts`) — one signer,
// one key set, no forked crypto. Its payload is the `PolicyBundle` below, plus the standard `iss`
// (the api's origin) and `aud` (`POLICY_BUNDLE_AUDIENCE`) claims the station checks, the way the realtime
// verifier checks a token's `iss`/`aud`. The station reads `PolicyBundle.stationId` back against its own
// identity, and `PolicyBundle.version` to tell a new bundle from the one it already holds. The bundle is
// **not** expired the way a token is: it is the persisted state a station applies on boot while offline
// (spec/10 §6.1), so the station verifies the signature and the binding, never an `exp`.
//
// ---------------------------------------------------------------------------------------------
// Extensibility (spec/10 §6.2, §7; F-CAT-02, F-CAT-05)
// ---------------------------------------------------------------------------------------------
//
// This same signed transport is what the **library bundle** (F-CAT-02) and F-CAT-05's launch will reuse
// to resolve a `gameId → LaunchRecipe` on the station. The envelope carries the policy now and names the
// `libraryVersion` the station should hold; the library **payload** itself is not built here (it rides
// `station.library.update`, its own signed file), and a later change adds a sibling `library` section
// without touching the signature path.
import { z } from "../zod.js";
import { StationIdSchema } from "../ids/schema.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { realtimeMessage } from "./envelope.js";
/**
 * The `aud` the signed bundle carries and the station requires (spec/07 §5 pattern). A bundle is for a
 * station, not for the api or the gateway, so its audience is its own — a device or realtime token
 * (`api`/`realtime`) can never be mistaken for a bundle, and a bundle can never authenticate an api call.
 */
export const POLICY_BUNDLE_AUDIENCE = 'station-policy';
/** The bundle version string: a content hash the station compares to tell a new bundle from its own (spec/10 §6.2). */
export const PolicyBundleVersionSchema = z
    .string()
    .min(1)
    .max(64)
    .openapi('PolicyBundleVersion', {
    description: 'The policy bundle version — a content-derived token; a station applies a bundle only when this differs from the one it holds (spec/10 §6.2).',
    example: 'pb1_3n8x2k9q',
});
/** The execution-guard allow-list posture (spec/11 §3.4, §10 `locker.executionPolicy`). */
export const PolicyExecutionPolicySchema = z.enum(['deny_list', 'allow_list']).openapi('PolicyExecutionPolicy', {
    description: 'Whether the execution guard denies only the always-blocked tools (`deny_list`, default) or allows only library entries and system hosts (`allow_list`) (spec/11 §3.4).',
});
/** How removable storage is treated for the player (spec/11 §4.1, §10 `locker.usbStorage`). */
export const PolicyUsbStorageSchema = z.enum(['block', 'read_only', 'allow']).openapi('PolicyUsbStorage', {
    description: 'Removable storage: `block` denies all (default), `read_only` write-protects, `allow` lifts the restriction (spec/11 §4.1).',
});
/** What is cleared between players (spec/11 §5, `cleanup.profile`). */
export const PolicyCleanupProfileSchema = z.enum(['light', 'full', 'off']).openapi('PolicyCleanupProfile', {
    description: 'Session hygiene profile: `light` (default), `full`, or `off` (spec/11 §5).',
});
/** What the station does when the connection drops mid-session (spec/05 §8, `sessions.offlinePolicy`). */
export const PolicyOfflinePolicySchema = z
    .enum(['continue_to_lease_end', 'lock_at_lease_end', 'lock_immediately'])
    .openapi('PolicyOfflinePolicy', {
    description: 'Offline behaviour mid-session: play to the signed lease end (default), lock at lease end, or lock immediately (spec/05 §8).',
});
/** The resolved locker policy (spec/11 §3, §4, §10) carried in the bundle. */
export const PolicyBundleLockerSchema = z
    .object({
    executionPolicy: PolicyExecutionPolicySchema,
    /** Images blocked on top of the always-blocked catalogue (spec/11 §3.4, §10 `locker.extraBlockedImages`), normalised without `.exe`. */
    extraBlockedImages: z.array(z.string().min(1).max(128)).max(200),
    /** Whether a constrained Explorer runs for this class (spec/11 §3.3, §10 `locker.allowExplorer`). */
    allowExplorer: z.boolean(),
    /** Whether Alt+F4 is swallowed in a session (spec/11 §3.1, §10 `locker.blockAltF4InSession`). */
    blockAltF4InSession: z.boolean(),
    usbStorage: PolicyUsbStorageSchema,
    /** The Settings-app pages the player may open (spec/11 §4.2, §10 `locker.allowedSettingsPages`). */
    allowedSettingsPages: z.array(z.string().min(1).max(64)).max(64),
    /**
     * How long a maintenance mode opened at the PC lasts before it relocks, in minutes (spec/11 §7, §10
     * `locker.maintenanceMinutes`, default 30, at most 240). Optional so a bundle persisted by an older api still
     * parses; absent means the default.
     */
    maintenanceMinutes: z.int().min(1).max(240).optional(),
    /**
     * Browser extension ids allowed to install on top of the block-all default (spec/11 §4.1, §10
     * `locker.extensionAllowlist`): Chrome Web Store / Edge Add-on ids written to `ExtensionInstallAllowlist`.
     * Optional so a bundle persisted by an older api still parses; absent (or empty) means today's behaviour —
     * every extension install is blocked.
     */
    extensionAllowlist: z.array(z.string().trim().min(1).max(64)).max(100).optional(),
})
    .meta({ id: 'PolicyBundleLocker' });
/** The remote-support posture the station enforces (spec/10 §5.6, spec/11 §10). */
export const PolicyBundleSupportSchema = z
    .object({
    /** Whether the agent draws "Staff are viewing this screen" while support views it (`support.notifyPlayerOnView`, default true). */
    notifyPlayerOnView: z.boolean(),
})
    .meta({ id: 'PolicyBundleSupport' });
/** The resolved session-cleanup rules (spec/11 §5, spec/10 §5.7). */
export const PolicyBundleCleanupSchema = z
    .object({
    profile: PolicyCleanupProfileSchema,
    /** Whether launchers signed in to venue-owned accounts are kept (spec/11 §5, `cleanup.keepLauncherAccounts`). */
    keepLauncherAccounts: z.boolean(),
    /** The cleanup budget in seconds (spec/10 §5.7, `cleanup.maxSeconds`). */
    maxSeconds: z.int().min(0).max(600),
    /**
     * The venue's game-save folder allow-list a `full` wipe keeps (spec/11 §5, `cleanup.gameSaveAllowList`): folder
     * paths (possibly with the station's path variables) under the player's shell folders that survive the `full`
     * clear of Desktop/Documents/Pictures/Videos/Music. Optional so a bundle persisted by an older api still parses;
     * absent (or empty) means today's behaviour — a `full` wipe keeps nothing extra.
     */
    gameSaveAllowList: z.array(z.string().trim().min(1).max(260)).max(100).optional(),
})
    .meta({ id: 'PolicyBundleCleanup' });
/** One attract-media item shown on the lock screen (spec/10 §5.1). Referenced by content hash so the station caches it (spec/10 §6.4). */
export const PolicyBundleMediaSchema = z
    .object({
    /** The media's SHA-256, base64url — the `media_cache` key on the station (spec/10 §6.4). */
    sha256: z.string().min(1).max(64),
    /** Where the station fetches it if it is not already cached. */
    url: z.string().min(1).max(2048),
    kind: z.enum(['image', 'video']),
})
    .meta({ id: 'PolicyBundleMedia' });
/** The fewest and most minutes a start-up setup may hold a PC (`stations.setupMaxMinutes`, ADR-0055). */
export const STATION_SETUP_MAX_MINUTES_MIN = 5;
export const STATION_SETUP_MAX_MINUTES_MAX = 240;
/** The default time limit of a start-up setup, in minutes (`stations.setupMaxMinutes`, ADR-0055). */
export const STATION_SETUP_MAX_MINUTES_DEFAULT = 30;
/** "Set up before play" on (ADR-0055, F-STN-26): the time limit after which the PC opens for play whatever is left. */
export const PolicyBundleSetupBeforePlaySchema = z
    .object({
    /** The most minutes a start-up setup holds the PC (`stations.setupMaxMinutes`, default 30). */
    maxMinutes: z.int().min(STATION_SETUP_MAX_MINUTES_MIN).max(STATION_SETUP_MAX_MINUTES_MAX),
})
    .meta({ id: 'PolicyBundleSetupBeforePlay' });
/**
 * The policy bundle content — the JWS payload the station verifies and applies (spec/10 §6.2). The
 * station reads `stationId` back against its own identity and `version` to tell a new bundle from the
 * one it holds; the rest is the resolved posture it enforces. The signed envelope adds the `iss`/`aud`
 * JWT claims the station also checks (see the file header); those are not on this record.
 */
export const PolicyBundleSchema = z
    .object({
    /** The station this bundle is for (spec/10 §6.2): the station refuses a bundle bound to another. */
    stationId: StationIdSchema,
    version: PolicyBundleVersionSchema,
    /** When the api built the bundle, RFC 3339 UTC. */
    issuedAt: IsoDateTimeSchema,
    /** The library version the station should hold (spec/10 §6.2, §7.1); `null` while none is published. The library payload rides `station.library.update`, not this bundle (F-CAT-02). */
    libraryVersion: z.string().min(1).max(64).nullable(),
    locker: PolicyBundleLockerSchema,
    cleanup: PolicyBundleCleanupSchema,
    offlinePolicy: PolicyOfflinePolicySchema,
    /** Minutes before the funded end at which the player is warned (spec/05 §5, `sessions.warningMinutes`). */
    warningMinutes: z.array(z.int().min(1).max(1440)).max(6),
    /**
     * Which of the warnings the kiosk also reads out (spec/05 §5, `sessions.voiceWarnings`, default 10 and 5).
     * Optional so a bundle persisted by an older api still parses; absent means the default.
     */
    voiceWarningMinutes: z.array(z.int().min(1).max(1440)).max(6).optional(),
    /**
     * The GPU temperature above which the station raises `temp_high`, in °C (spec/10 §6.6, `station.alertGpuTempC`,
     * default 90). Optional so a bundle persisted by an older api still parses; absent means the default.
     */
    alertGpuTempC: z.int().min(50).max(120).optional(),
    /** The remote-support posture (spec/10 §5.6). Optional, like the fields above; absent means the defaults. */
    support: PolicyBundleSupportSchema.optional(),
    /**
     * The venue's `station.disklessMode` for this station (2026-10-07, spec/10 §14): `auto` (default — the station detects
     * a diskless CCBoot PC itself), `on` (treat it as diskless), `off` (treat it as having its own disk — exactly the
     * behaviour before diskless handling). A PC's own `disklessMode` in station.config.json wins over it. Carried only
     * when not `auto`, so a venue that never sets it keeps its bundle version; absent means `auto`.
     */
    disklessMode: z.enum(['auto', 'on', 'off']).optional(),
    /**
     * The venue's `stations.setupBeforePlay` for this station (2026-10-11, ADR-0055, F-STN-26): present when it is on —
     * a PC that starts while apps marked "Install for players" are missing on it installs them before it opens for play
     * (`setting_up`) — with its time limit, `stations.setupMaxMinutes`. Carried only when on, so a venue that never turns
     * it on keeps its bundle version; absent means off, today's behaviour (apps install only at idle moments).
     */
    setupBeforePlay: PolicyBundleSetupBeforePlaySchema.optional(),
    /** The attract media the lock screen cycles (spec/10 §5.1); empty when the venue set none. */
    attractMedia: z.array(PolicyBundleMediaSchema).max(200),
})
    .openapi('PolicyBundle', {
    description: 'The resolved policy a station enforces, signed by the api JWKS and applied transactionally (spec/10 §6.2, spec/11 §6, F-STN-09).',
});
// ---------------------------------------------------------------------------------------------
// `station.policy.update` (server → station, spec/09 §6.2; expiry 24 h in `commands.ts`)
// ---------------------------------------------------------------------------------------------
export const STATION_POLICY_UPDATE = 'station.policy.update';
/** The api's pull route for the calling station's signed bundle: what a pushed `station.policy.update` names as its `url` (spec/10 §6.2). */
export const POLICY_BUNDLE_PULL_PATH = '/v1/station/policy-bundle';
export const PolicyBundleUpdatePayloadSchema = z
    .object({
    /** The bundle version to have (spec/10 §6.2): the station skips the download and verify when it already holds this version. */
    version: PolicyBundleVersionSchema,
    /**
     * The signed bundle inline — the compact JWS (EdDSA) the station verifies against the api JWKS and
     * applies (spec/10 §6.2). Present on the inline delivery; absent when `url` carries a signed CDN copy
     * to download instead (either the inline bundle or a url, spec/09 §6.2 `station.policy.update`).
     */
    signedBundle: z.string().min(1).max(65536).optional(),
    /**
     * Where to fetch the bundle instead of carrying it inline (spec/09 §6.2): the push after a settings
     * change sends `POLICY_BUNDLE_PULL_PATH` (the worker that pushes holds no signing key, so it names the
     * version and the station pulls the signed bundle from its own api with its device token). The station
     * never follows a foreign host: it always pulls from the api it is paired with and verifies the signature,
     * so the url is a hint, not a trust.
     */
    url: z.string().min(1).max(2048).optional(),
})
    .refine((update) => update.signedBundle !== undefined || update.url !== undefined, {
    message: 'A policy update carries the signed bundle inline or a url to download it (spec/09 §6.2).',
})
    .openapi('PolicyBundleUpdatePayload', {
    description: 'Server → station: apply the policy bundle at `version`, delivered inline (`signedBundle`) or by `url` (spec/09 §6.2, spec/10 §6.2, F-STN-09).',
});
/** The framed `station.policy.update` command (spec/09 §5.1, §6.2). */
export const PolicyBundleUpdateMessage = realtimeMessage('cmd', STATION_POLICY_UPDATE, PolicyBundleUpdatePayloadSchema, 'PolicyBundleUpdateMessage');
//# sourceMappingURL=policy-bundle.js.map